/**
 * RBAC permission ids used by People Space views and nav config.
 *
 * One place, because a permission string is only useful if the nav item, the
 * route guard and the proxy all spell it identically — a typo here does not
 * fail loudly, it silently hides a page from everyone or shows it to everyone.
 * The proxy's copy lives in `proxy-server/lib/rbac.js#RBAC_KNOWN_PERMISSIONS`.
 */

/**
 * Candidate Communication — the `candidate-communication` sidebar subgroup:
 * TA Action Boards, Interview Links, Log, Email Library. Held by members of the
 * `cca.peoplespace@finn.com` Google Workspace group.
 */
export const CANDIDATE_COMMS_VIEW = 'space.people.candidate_comms.view';

/**
 * Cora — make her write to Lever on demand: screen a named candidate or re-run one in live mode.
 * Reading Cora's runs and previewing a screening need only CANDIDATE_COMMS_VIEW. Held by the
 * `cora-operators` RBAC group (CORA_OPERATORS_GOOGLE_GROUPS) and core admins; always enforced.
 */
export const CORA_WRITE = 'space.people.cora.write';

/**
 * Chat with Boris — gated independently of Ticket Management/My Inquiries (which use
 * `space.people.ticket_management.view`), even though it currently maps onto the same
 * `people.business-partnering@finn.com` Google Workspace group. The proxy's copy of this
 * permission lives in `proxy-server/lib/rbac.js#RBAC_ROLE_BORIS_VIEWER`.
 */
export const BORIS_VIEW = 'space.people.chat_with_boris.view';

/**
 * Reference Letters — supervisor gradings and the letters Boris drafts from them. Held by the
 * `people.business-partnering@finn.com` Google Workspace group. ALWAYS enforced by the proxy
 * (`routes/referenceLetters.js`), so the page guard is `strict` to match.
 */
export const REFERENCE_LETTERS_DRAFT = 'space.people.reference_letters.draft';

/**
 * Internal Playground — the Org Planning Overview and the Role Approvals page.
 *
 * Granted by the Google-Group-backed `org-planning-viewers` role (squad-org-planning@finn.com, one
 * Workspace group the People team manages with no PR and no deploy) plus core admins. The per-user
 * RBAC overrides it started with (`INTERNAL_PLAYGROUND_ADMIN_EMAILS` in `proxy-server/lib/rbac.js`)
 * are gone: that list is empty since Oct 19 2026.
 *
 * ⚠️ This is READ access to both pages. It says nothing about who may approve a role: that is the
 * three named approvers, decided per request inside the proxy (`roleApprovals.decisionRefusal`),
 * and no amount of this permission confers it.
 */
export const INTERNAL_PLAYGROUND_VIEW = 'space.people.internal_playground.view';

/**
 * Org Planning PROPOSERS (`org-planning-proposers`, squad-org-planning-extended@finn.com): the Org
 * Planning Overview for a wider audience. They see the whole tree, but propose, edit and decide
 * backfills only in their own reporting line, and see a salary figure only where Salary Bands
 * would show it to them. NOT Role Approvals. Every one of those limits is enforced in the proxy
 * (`proxy-server/lib/orgPlanningViewer.js`); the page only stops offering what would be refused.
 */
export const INTERNAL_PLAYGROUND_PROPOSE = 'space.people.internal_playground.propose';

/** Either permission opens the Org Planning Overview. */
export const ORG_PLANNING_OVERVIEW_ACCESS = [INTERNAL_PLAYGROUND_VIEW, INTERNAL_PLAYGROUND_PROPOSE] as const;

/**
 * JobAuto Rechner — payslip parsing and the colleague subscription lookup. Held by the
 * `jobauto-users` RBAC group (JOBAUTO_GOOGLE_GROUPS) and, until that group exists, by the four
 * per-user overrides in `proxy-server/lib/rbac.js#JOBAUTO_USER_EMAILS` — the list that used to be
 * hard-coded in `JobAutoGuard.tsx`. The proxy enforces it on all of `/api/jobauto` (PLA-626).
 */
export const JOBAUTO_USE = 'space.people.jobauto.use';

/**
 * Onboarding Scheduling — the incoming-hire roster and the Get-to-Know send. Held by the
 * `onboarding-viewers` RBAC group (ONBOARDING_GOOGLE_GROUPS) and, until that group is mapped, by
 * the per-user overrides in `proxy-server/lib/rbac.js#ONBOARDING_USER_EMAILS` — everyone who used
 * the page between 2026-08-12 and 2026-10-01. The proxy enforces it with `alwaysEnforce` on all of
 * `/api/onboarding` (PLA-626), so the route guard is `strict` and the nav item carries it too.
 */
export const ONBOARDING_VIEW = 'space.people.onboarding.view';

/**
 * People Cockpit — attrition, forward-looking attrition and probation success. Held ONLY by the
 * per-user overrides in `proxy-server/lib/rbac.js#PEOPLE_COCKPIT_USER_EMAILS` (not core admins).
 * The proxy enforces it with `alwaysEnforce` on all of `/api/people-cockpit`, so the route guard is
 * `strict` and the nav item carries it too.
 */
export const PEOPLE_COCKPIT_VIEW = 'space.people.people_cockpit.view';
